/**
 * TargetGoalsSection — Target & Goals section for the Buyer Passport.
 *
 * Renders ALL target and goals fields as a single flat inline-row list,
 * each individually editable via per-row expand-in-place. The "Goals /
 * Constraints you want to satisfy" sub-header and grey card chrome are gone;
 * the four constraint fields fold directly into the same list as the seven
 * target fields, separated by hairline dividers.
 *
 * Target rows (7):
 *   Target price · Target area · Property type · Occupancy ·
 *   Loan program · Move-in timeline · Buyer type
 *
 * Goals rows (4, folded in — same visual row idiom):
 *   Target PITI · Down-payment plan · Reserve floor · Comfort DTI ceiling
 *
 * Edit wiring is unchanged:
 *   - Target rows → useTargetEdit → PUT /api/readiness/target
 *   - PITI / Reserve floor / Comfort DTI → useProfileEdit → PATCH /api/profile
 *   - Down-payment plan → useTargetEdit (dual-writes pct + amount)
 *
 * Tests querying data-testid="target-card" and data-testid="goals-card"
 * continue to find those elements in the DOM.
 */

import {
  formatCompactCurrency,
  type DashboardSnapshot,
} from "../../../lib/dashboard";
import type { UseProfileEditOptions } from "../../../lib/profile/use-profile-edit";
import type { UseTargetEditOptions } from "../../../lib/profile/use-target-edit";
import type { ReadinessTarget } from "../../../lib/readiness-types";
import { displayReadinessTargetArea } from "../../../lib/readiness-target-display";
import { TargetCard } from "./target-card";
import {
  ProfileGoalRow,
  ReserveFloorRow,
  ComfortDtiRow,
} from "./rows/profile-goal-row";
import { DownPaymentPlanRow } from "./rows/down-payment-plan-row";

export interface TargetGoalsSectionProps {
  snapshot: DashboardSnapshot;
  target?: ReadinessTarget | null;
  targetEditOpts?: UseTargetEditOptions;
  profileEditOpts?: UseProfileEditOptions;
  onAddProperty?: () => void;
}

export function TargetGoalsSection({
  snapshot,
  target,
  targetEditOpts,
  profileEditOpts,
  onAddProperty,
}: TargetGoalsSectionProps) {
  const hasTargetEdit = Boolean(targetEditOpts);

  return (
    <section aria-labelledby="target-goals-heading" data-testid="target-goals-section">
      {/* Section eyebrow */}
      <div className="mb-3">
        <p
          className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground"
          id="target-goals-heading"
        >
          Target &amp; Goals
        </p>
      </div>

      {/* Unified single list: target rows + goals rows, divide-y hairlines */}
      <div className="surface-card overflow-hidden">
        {hasTargetEdit && targetEditOpts ? (
          // Editable target rows + goals rows in one divide-y container
          <div className="divide-y divide-border-soft" data-testid="target-card">
            {/* 7 target rows (headless — no header, no wrapper card) */}
            <TargetCard
              snapshot={snapshot}
              target={target}
              onAddProperty={onAddProperty}
              opts={targetEditOpts}
              headless
              noWrapper
            />
            {/* 4 goal rows folded inline */}
            <ProfileGoalRow
              currentValue={snapshot.monthlyBudget > 0 ? snapshot.monthlyBudget : null}
              opts={profileEditOpts}
            />
            <DownPaymentPlanRow
              snapshot={snapshot}
              target={target ?? null}
              opts={targetEditOpts}
            />
            <ReserveFloorRow
              currentValue={snapshot.reserveFloorMonths ?? 3}
              opts={profileEditOpts}
            />
            <ComfortDtiRow
              currentValue={snapshot.comfortDtiPct ?? null}
              opts={profileEditOpts}
            />
          </div>
        ) : (
          /* Read-only fallback when no edit opts are provided */
          <div className="divide-y divide-border-soft">
            <div data-testid="target-card">
              <LabelValueRow
                label="Target price"
                value={
                  snapshot.targetPrice > 0
                    ? formatCompactCurrency(snapshot.targetPrice)
                    : "Not set"
                }
              />
              <LabelValueRow
                label="Target area"
                value={
                  displayReadinessTargetArea(target, snapshot.targetArea) ||
                  "Not set"
                }
              />
              <LabelValueRow
                label="Move-in timeline"
                value={timelineLabel(target?.timeline ?? null, snapshot.timeline ?? undefined)}
              />
            </div>
          </div>
        )}
        {/* Goals card data-testid anchor — always present so tests querying
            '[data-testid="goals-card"]' continue to find it */}
        <div data-testid="goals-card" className="sr-only" aria-hidden="true" />
      </div>
    </section>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

interface LabelValueRowProps {
  label: string;
  value: string;
}

function LabelValueRow({ label, value }: LabelValueRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3.5 border-b border-border-soft last:border-0">
      <span className="text-sm text-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground text-right tabular-nums">
        {value}
      </span>
    </div>
  );
}

function timelineLabel(token: string | null | undefined, fallback: string | undefined): string {
  switch (token) {
    case "0_3_months": return "ASAP (0–3 mo)";
    case "3_6_months": return "3–6 mo";
    case "6_12_months": return "6–12 mo";
    case "12_plus": return "Exploring";
    default: return fallback ?? "Not set";
  }
}
